/**
 * WorkspaceConnectorSettings Page
 * 
 * Configuration form for a workspace-level connector.
 * Shows the form fields with inline setup instructions and a delete option.
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageShell, PageHeader } from '@/components/shared/layout'
import { settingsButtonClass, settingsDangerOutlineClass } from '@/components/shared/settings/SettingsKit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { BrandIcon, BRAND_ICONS } from '@/components/ui/brand-icons'
import { toast } from 'sonner'
import {
  saveRevenueConfig,
  testRevenueConfig,
  disconnectRevenueConfig,
  getFoxyWebhookInfo,
  getJotformWebhookInfo,
  getTypeformWebhookInfo,
  saveTypeformConfig,
  testTypeformConfig,
  getTallyWebhookInfo,
  getWebflowWebhookInfo,
  saveWebflowConfig,
  testWebflowConfig,
  getRevenueConfig,
} from '@/lib/api/api'
import { getWorkspaceRevenueConfig } from '@/lib/api/workspace-team'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { STRIPE_CONNECTOR_CONFIG, FOXY_CONNECTOR_CONFIG, JOTFORM_CONNECTOR_CONFIG, TYPEFORM_CONNECTOR_CONFIG, TALLY_CONNECTOR_CONFIG, WEBFLOW_CONNECTOR_CONFIG } from '@/lib/config/connectors'
import type { ConnectorConfig, ConnectorField } from '@/lib/types/connector'
import type { WorkspaceRevenueConfig } from '@/lib/types/workspace-team'
import { getConnectorErrorMessage, formatProviderName } from '@/lib/utils/connectorErrors'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ClipboardIcon,
  LinkSquare01Icon,
  Loading01Icon,
  ShieldKeyIcon,
  Tick01Icon,
} from "@hugeicons/core-free-icons"

type ConnectorType = 'stripe' | 'foxy' | 'jotform' | 'typeform' | 'tally' | 'webflow'

// All forms connectors (skip revenue config)
const FORMS_CONNECTOR_TYPES: ConnectorType[] = ['jotform', 'typeform', 'tally', 'webflow']
// Webhook-only forms connectors (always active, no credentials to save)
const WEBHOOK_ONLY_FORMS: ConnectorType[] = ['jotform', 'tally']

const CONNECTOR_CONFIGS: Record<ConnectorType, Omit<ConnectorConfig, 'status' | 'enabled'>> = {
  stripe: STRIPE_CONNECTOR_CONFIG,
  foxy: FOXY_CONNECTOR_CONFIG,
  jotform: JOTFORM_CONNECTOR_CONFIG,
  typeform: TYPEFORM_CONNECTOR_CONFIG,
  tally: TALLY_CONNECTOR_CONFIG,
  webflow: WEBFLOW_CONNECTOR_CONFIG,
}

export default function WorkspaceConnectorSettings() {
  const { connectorType } = useParams<{ connectorType: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { workspace, properties, refetch } = useWorkspace()

  // Get first property for API calls
  const primaryPropertyId = properties[0]?.propertyId || ''

  // Validate connector type
  const validConnectorType = connectorType as ConnectorType
  const config = CONNECTOR_CONFIGS[validConnectorType]

  const isFormsConnector = FORMS_CONNECTOR_TYPES.includes(validConnectorType)
  const isWebhookOnly = WEBHOOK_ONLY_FORMS.includes(validConnectorType)

  // Revenue config state
  const [revenueConfig, setRevenueConfig] = useState<WorkspaceRevenueConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(!isFormsConnector)

  // Form state
  const [values, setValues] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})
  const [error, setError] = useState('')
  const [testSuccess, setTestSuccess] = useState(false)

  // Disconnect dialog state
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  // Load workspace revenue configuration (only for revenue connectors)
  // Falls back to property-level config if workspace endpoint returns empty
  const loadRevenueConfig = useCallback(async () => {
    if (!workspace?.workspaceId || !token || isFormsConnector) return

    setIsLoadingConfig(true)
    try {
      let config = await getWorkspaceRevenueConfig(workspace.workspaceId, token)

      // Fallback only when workspace response is structurally empty.
      const hasWorkspaceRevenueShape = !!(config && ('stripe' in config || 'foxy' in config))
      if (!hasWorkspaceRevenueShape && primaryPropertyId) {
        const propConfig = await getRevenueConfig(primaryPropertyId, token)
        if (propConfig?.revenueSetup) {
          config = {
            stripe: propConfig.revenueSetup.stripe,
            foxy: propConfig.revenueSetup.foxy,
          }
        }
      }

      setRevenueConfig(config)
    } catch (error) {
      console.error('Failed to load revenue config:', error)
    } finally {
      setIsLoadingConfig(false)
    }
  }, [workspace?.workspaceId, token, isFormsConnector, primaryPropertyId])

  useEffect(() => {
    if (!isFormsConnector) loadRevenueConfig()
  }, [loadRevenueConfig, isFormsConnector])

  // Initialize form values when config loads
  useEffect(() => {
    if (config && revenueConfig) {
      const initial: Record<string, string> = {}
      config.fields.forEach((field) => {
        if (validConnectorType === 'stripe') {
          initial[field.id] = ''
        } else if (validConnectorType === 'foxy' && revenueConfig.foxy) {
          if (field.id === 'storeUrl') {
            initial[field.id] = revenueConfig.foxy.store_url || ''
          } else {
            initial[field.id] = ''
          }
        } else {
          initial[field.id] = ''
        }
      })
      setValues(initial)
    }
  }, [config, revenueConfig, validConnectorType])

  // Load dynamic fields (e.g., webhook URLs)
  useEffect(() => {
    if (validConnectorType === 'foxy' && primaryPropertyId && token) {
      setLoadingFields((prev) => ({ ...prev, webhookUrl: true }))
      getFoxyWebhookInfo(primaryPropertyId, token)
        .then((info) => {
          if (info?.webhookUrl) {
            setValues((prev) => ({ ...prev, webhookUrl: info.webhookUrl }))
          }
        })
        .catch((err) => console.error('Failed to load Foxy webhook URL:', err))
        .finally(() => {
          setLoadingFields((prev) => ({ ...prev, webhookUrl: false }))
        })
    }
    if (validConnectorType === 'jotform' && primaryPropertyId && token) {
      setLoadingFields((prev) => ({ ...prev, webhookUrl: true }))
      getJotformWebhookInfo(primaryPropertyId, token)
        .then((info) => {
          if (info?.webhookUrl) {
            setValues((prev) => ({ ...prev, webhookUrl: info.webhookUrl }))
          }
        })
        .catch((err) => console.error('Failed to load JotForm webhook URL:', err))
        .finally(() => {
          setLoadingFields((prev) => ({ ...prev, webhookUrl: false }))
        })
    }
    if (validConnectorType === 'typeform' && primaryPropertyId && token) {
      setLoadingFields((prev) => ({ ...prev, webhookUrl: true }))
      getTypeformWebhookInfo(primaryPropertyId, token)
        .then((info) => {
          if (info?.webhookUrl) {
            setValues((prev) => ({ ...prev, webhookUrl: info.webhookUrl }))
          }
        })
        .catch((err) => console.error('Failed to load Typeform webhook URL:', err))
        .finally(() => {
          setLoadingFields((prev) => ({ ...prev, webhookUrl: false }))
        })
    }
    if (validConnectorType === 'tally' && primaryPropertyId && token) {
      setLoadingFields((prev) => ({ ...prev, webhookUrl: true }))
      getTallyWebhookInfo(primaryPropertyId, token)
        .then((info) => {
          if (info?.webhookUrl) {
            setValues((prev) => ({ ...prev, webhookUrl: info.webhookUrl }))
          }
        })
        .catch((err) => console.error('Failed to load Tally webhook URL:', err))
        .finally(() => {
          setLoadingFields((prev) => ({ ...prev, webhookUrl: false }))
        })
    }
    if (validConnectorType === 'webflow' && primaryPropertyId && token) {
      setLoadingFields((prev) => ({ ...prev, webhookUrl: true }))
      getWebflowWebhookInfo(primaryPropertyId, token)
        .then((info) => {
          if (info?.webhookUrl) {
            setValues((prev) => ({ ...prev, webhookUrl: info.webhookUrl }))
          }
        })
        .catch((err) => console.error('Failed to load Webflow webhook URL:', err))
        .finally(() => {
          setLoadingFields((prev) => ({ ...prev, webhookUrl: false }))
        })
    }
  }, [validConnectorType, primaryPropertyId, token])

  // Determine if connector is connected
  // Webhook-only forms connectors are always active
  const isConnected = useMemo(() => {
    if (isWebhookOnly) return true
    if (isFormsConnector) return false // typeform/webflow: not connected until credentials saved
    if (!revenueConfig) return false
    if (validConnectorType === 'stripe') return !!revenueConfig.stripe?.configured
    if (validConnectorType === 'foxy') return !!revenueConfig.foxy?.configured
    return false
  }, [revenueConfig, validConnectorType, isFormsConnector, isWebhookOnly])

  // Handle input change
  const handleChange = useCallback((fieldId: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }))
    setError('')
    setTestSuccess(false)
  }, [])

  // Handle copy to clipboard
  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value)
    toast.success('Copied to clipboard')
  }

  // Validate fields
  const validate = useCallback((): string | null => {
    if (!config) return 'Invalid connector type'
    for (const field of config.fields) {
      if (field.required && !values[field.id]?.trim()) {
        return `${field.label} is required`
      }
      if (field.pattern && values[field.id]) {
        const regex = new RegExp(field.pattern)
        if (!regex.test(values[field.id])) {
          return field.patternError || `Invalid ${field.label.toLowerCase()}`
        }
      }
    }
    return null
  }, [config, values])

  // Handle save
  const handleSave = async () => {
    if (!config || !primaryPropertyId || isWebhookOnly) return

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError('')

    try {
      if (validConnectorType === 'stripe') {
        await saveRevenueConfig(primaryPropertyId, 'stripe', {
          method: 'api',
          apiKey: values.apiKey,
        }, token!)
      } else if (validConnectorType === 'foxy') {
        await saveRevenueConfig(primaryPropertyId, 'foxy', {
          storeUrl: values.storeUrl,
          encryptionKey: values.encryptionKey,
        }, token!)
      } else if (validConnectorType === 'typeform') {
        await saveTypeformConfig(primaryPropertyId, values.apiToken, token!)
      } else if (validConnectorType === 'webflow') {
        await saveWebflowConfig(primaryPropertyId, values.siteId, values.apiToken, token!)
      }

      const description = isFormsConnector ? 'Form submission tracking is now enabled.' : 'Revenue tracking is now enabled for this workspace.'
      toast.success(`${config.name} ${isConnected ? 'updated' : 'connected'} successfully`, {
        description,
      })

      if (!isFormsConnector) {
        await loadRevenueConfig()
        refetch()
      }
    } catch (err) {
      setError(getConnectorErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  // Handle test connection
  const handleTest = async () => {
    if (!config?.supportsTest || !primaryPropertyId) return

    setIsTesting(true)
    setError('')
    setTestSuccess(false)

    try {
      let result: { success: boolean; message?: string }
      if (validConnectorType === 'typeform') {
        result = await testTypeformConfig(primaryPropertyId, token!)
      } else if (validConnectorType === 'webflow') {
        result = await testWebflowConfig(primaryPropertyId, token!)
      } else if (validConnectorType === 'stripe') {
        const apiKey = values.apiKey?.trim()
        result = await testRevenueConfig(
          primaryPropertyId,
          'stripe',
          token!,
          apiKey ? { method: 'api', apiKey } : undefined
        )
      } else if (validConnectorType === 'foxy') {
        const storeUrl = values.storeUrl?.trim()
        const encryptionKey = values.encryptionKey?.trim()
        result = await testRevenueConfig(
          primaryPropertyId,
          'foxy',
          token!,
          storeUrl || encryptionKey ? { storeUrl, encryptionKey } : undefined
        )
      } else {
        result = await testRevenueConfig(primaryPropertyId, validConnectorType, token!)
      }
      if (result.success) {
        setTestSuccess(true)
        toast.success('Connection test successful')
      } else {
        setError(result.message || 'Connection test failed')
      }
    } catch (err) {
      setError(getConnectorErrorMessage(err))
    } finally {
      setIsTesting(false)
    }
  }

  // Handle disconnect
  const confirmDisconnect = async () => {
    if (!primaryPropertyId || !token) return

    setIsDisconnecting(true)
    try {
      await disconnectRevenueConfig(primaryPropertyId, validConnectorType, token)
      toast.success(
        `${formatProviderName(validConnectorType)} disconnected`,
        { description: 'Revenue tracking has been disabled for this workspace.' }
      )
      refetch()
      navigate('..', { relative: 'path' })
    } catch (error) {
      toast.error('Failed to disconnect', {
        description: getConnectorErrorMessage(error),
      })
    } finally {
      setIsDisconnecting(false)
      setDisconnectDialogOpen(false)
    }
  }

  // Handle back navigation
  const handleBack = () => {
    navigate('..', { relative: 'path' })
  }

  // Render a field
  const renderField = (field: ConnectorField) => {
    const isLoadingField = loadingFields[field.id]
    const fieldValue = values[field.id] || ''

    return (
      <div key={field.id} className="flex flex-col gap-[7px]">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={field.id} className="text-xs font-medium leading-4 text-foreground">
            {field.label}
            {field.required && !isConnected && <span className="ml-1 text-foreground/40">required</span>}
          </Label>
          {field.externalLink && (
            <a
              href={field.externalLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-medium leading-4 text-foreground underline underline-offset-2 hover:text-foreground/70"
            >
              {field.externalLink.label}
              <HugeiconsIcon icon={LinkSquare01Icon} size={12} />
            </a>
          )}
        </div>

        {isLoadingField ? (
          <Skeleton className="h-10 w-full" />
        ) : field.type === 'readonly' ? (
          <button
            type="button"
            className={cn(
              'flex h-10 items-center gap-2 rounded-lg border border-input bg-muted/50 px-3 text-left text-[13px] transition-colors hover:bg-muted',
              field.monospace && 'font-mono text-xs',
            )}
            onClick={() => fieldValue && handleCopy(fieldValue)}
          >
            <span className="flex-1 truncate text-foreground/70">{fieldValue || field.placeholder}</span>
            <HugeiconsIcon icon={ClipboardIcon} size={14} className="shrink-0 text-foreground/40" />
          </button>
        ) : (
          <Input
            id={field.id}
            type={field.type === 'password' ? 'password' : 'text'}
            placeholder={
              field.type === 'password' && validConnectorType === 'stripe' && revenueConfig?.stripe?.api_key_masked
                ? revenueConfig.stripe.api_key_masked
                : field.placeholder
            }
            value={fieldValue}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={cn('h-10 rounded-lg px-3 text-[13px]', field.monospace && 'font-mono text-xs')}
          />
        )}

        {field.helpText && <p className="text-xs leading-4 text-foreground/60">{field.helpText}</p>}
      </div>
    )
  }

  const shell = (children: ReactNode) => <PageShell>{children}</PageShell>

  // If invalid connector type, show error
  if (!config) {
    return shell(
      <>
        <PageHeader title="Connector not found" description={`"${connectorType}" isn't a connector Humblytics supports.`} />
        <Button variant="outline" onClick={handleBack} className={settingsButtonClass}>
          Back to connectors
        </Button>
      </>,
    )
  }

  const brandColor = BRAND_ICONS[config.brandKey]?.color
  const hasChanges = !isWebhookOnly && config.fields.some(
    (f) => f.type !== 'readonly' && values[f.id]?.trim()
  )
  const setupSteps = config.helpContent.setupSteps

  return shell(
    <>
      <div className="mb-[18px] flex items-center gap-3.5">
        <div
          className="flex size-11 shrink-0 items-center justify-center rounded-[10px]"
          style={{ backgroundColor: brandColor }}
        >
          <BrandIcon brand={config.brandKey} className="size-6" fill="#ffffff" />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{config.name}</h1>
            <span className="inline-flex h-[22px] items-center gap-1.5 rounded-md bg-canvas px-2 text-xs font-medium leading-4 text-foreground/80">
              <span className={cn('size-1.5 rounded-full', isConnected ? 'bg-live' : 'bg-foreground/30')} />
              {isWebhookOnly ? 'Active' : isConnected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          <p className="text-[13px] leading-4 text-foreground/60">
            {isFormsConnector
              ? `Matches ${config.name} submits to sessions Humblytics already tracks.`
              : `Matches ${config.name} payments to visits on your site.`}
          </p>
        </div>
      </div>

      {isLoadingConfig ? (
        <div className="well w-full max-w-[720px]">
          <div className="surface-raised flex flex-col gap-4 p-5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      ) : (
        <div className="well flex w-full max-w-[720px] flex-col gap-2">
          <section className="surface-raised flex flex-col overflow-clip">
            <div className="flex flex-col gap-[3px] px-5 pb-3 pt-[18px]">
              <h2 className="text-sm font-semibold leading-[18px] text-foreground">
                {isWebhookOnly ? 'Webhook setup' : isConnected ? 'Update configuration' : `Connect ${config.name}`}
              </h2>
              <p className="text-xs leading-4 text-foreground/60">
                {isWebhookOnly
                  ? `Copy the webhook URL and add it to your ${config.name} account.`
                  : isFormsConnector
                    ? `Enter your ${config.name} credentials to start receiving form submissions.`
                    : isConnected
                      ? 'Leave the key empty to keep the current one.'
                      : 'Paste your credentials to start matching revenue.'}
              </p>
            </div>

            <div className="flex flex-col gap-4 px-5 pb-5">
              {config.fields.map((field) => (
                <div key={field.id} className="flex flex-col gap-4">
                  {renderField(field)}
                  {field.id === 'webhookUrl' && setupSteps && <SetupSteps steps={setupSteps} />}
                </div>
              ))}

              {validConnectorType === 'stripe' && setupSteps && !isConnected && <SetupSteps steps={setupSteps} />}

              {config.helpContent.privacyNote && (
                <p className="flex gap-2.5 rounded-lg bg-muted/50 px-3.5 py-3 text-xs leading-[18px] text-foreground/70">
                  <HugeiconsIcon icon={ShieldKeyIcon} size={14} className="mt-0.5 shrink-0 text-foreground/50" />
                  {config.helpContent.privacyNote}
                </p>
              )}

              {error && (
                <p role="alert" className="rounded-lg bg-danger-tint px-3.5 py-3 text-xs leading-[18px] text-danger">
                  {error}
                </p>
              )}
              {testSuccess && (
                <p role="status" className="flex items-center gap-2 rounded-lg bg-muted/50 px-3.5 py-3 text-xs leading-[18px] text-foreground">
                  <HugeiconsIcon icon={Tick01Icon} size={14} className="text-success" />
                  Connection works. {config.name} is sending data.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 border-t border-border bg-muted/30 px-5 py-3">
              {isWebhookOnly ? (
                <Button variant="outline" onClick={handleBack} className={`${settingsButtonClass} ml-auto`}>
                  Done
                </Button>
              ) : (
                <>
                  {isConnected && config.supportsTest && (
                    <Button
                      variant="outline"
                      onClick={handleTest}
                      disabled={isTesting || isLoading}
                      className={`${settingsButtonClass} gap-1.5`}
                    >
                      {isTesting && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
                      {isTesting ? 'Testing…' : 'Test connection'}
                    </Button>
                  )}
                  <Button
                    onClick={handleSave}
                    disabled={isLoading || !hasChanges}
                    className="ml-auto h-[34px] gap-1.5 rounded-lg px-3.5 text-[13px] font-medium shadow-none"
                  >
                    {isLoading && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
                    {isLoading ? 'Saving…' : isConnected ? 'Save changes' : `Connect ${config.name}`}
                  </Button>
                </>
              )}
            </div>
          </section>

          {isConnected && !isFormsConnector && !isWebhookOnly && (
            <section className="surface-raised flex items-center gap-4 px-5 py-4">
              <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <h2 className="text-sm font-semibold leading-[18px] text-foreground">Disconnect {config.name}</h2>
                <p className="text-xs leading-4 text-foreground/60">
                  Stops matching {config.name} revenue. Revenue data already collected is kept.
                </p>
              </div>
              <Button variant="outline" onClick={() => setDisconnectDialogOpen(true)} className={settingsDangerOutlineClass}>
                Disconnect {config.name}
              </Button>
            </section>
          )}
        </div>
      )}

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect {config.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {config.name} integration from this workspace 
              and stop tracking revenue from this provider. Your existing revenue data will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              disabled={isDisconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDisconnecting ? (
                <span className="flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Disconnecting…
                </span>
              ) : (
                `Disconnect ${config.name}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>,
  )
}

function SetupSteps({ steps }: { steps: string[] }) {
  return (
    <ol className="flex flex-col gap-2.5 rounded-lg bg-muted/50 px-3.5 py-3">
      {steps.map((step, i) => (
        <li key={step} className="flex gap-2.5 text-xs leading-[18px] text-foreground/70">
          <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-card text-[11px] font-semibold text-foreground">
            {i + 1}
          </span>
          {step}
        </li>
      ))}
    </ol>
  )
}



